import { Tooltip } from '@highlight/ui'
import { Button } from './Button'
import { twMerge } from 'tailwind-merge'
import { dismissNagBar, isNagBarDismissed } from '../../utils/paywallStorage'
import { CircleAlert, Info, X } from 'lucide-react'
import { useState, ReactNode, useCallback, useEffect, useMemo } from 'react'
import { useEnrichedBilling } from '../../hooks'
import { useCreditsTopUp } from '../../hooks/use-credits-top-up'
import { YellowWarningIcon, CircularProgressIcon } from '../icons/WarningIcons'
import { useUser } from '../../store'
import { useAudioNoteSettings } from '../../hooks/useAudioNoteSettings'

export interface NagBarProps {
  /**
   * Context identifier for where the nag bar is shown (e.g., 'chat', 'meeting-summary')
   * Used for dismissal tracking and analytics
   */
  context:
    | 'chat'
    | 'meeting-summary'
    | 'meeting-advanced-options'
    | 'meeting-detection'
    | 'summaries'
    | 'home'
    | 'audio-note'

  /**
   * Visual variant
   */
  variant?: 'warning' | 'blocked' | 'soft-blocked' | 'free-tier' | 'info' | 'promotional'

  /**
   * Whether the nag bar is dismissable
   */
  dismissable?: boolean

  /**
   * Optional className
   */
  className?: string

  /**
   * Custom icon (overrides default variant icon)
   */
  icon?: ReactNode

  /**
   * Optional promotional image URL (shown on the left when variant is 'promotional')
   */
  promotionalImage?: string

  /**
   * For audio-note context: when true, forces the local transcription nag to show.
   * When false/undefined, suppresses local transcription nag to show promo instead.
   */
  showLocalTranscriptionNag?: boolean
}

/**
 * NagBar - A smart, self-contained notification bar that automatically determines
 * what message to show based on the user's billing state and handles all interactions internally.
 *
 * Supports:
 * - Pro users at 80-99%: Warning to add credits
 * - Pro users at 100%: Blocked message to add credits
 * - Trial users who claimed the trial from onboarding: Explore benefits
 * - Legacy pro users on local transcription: Add credits
 */
export const NagBar: React.FC<NagBarProps> = ({
  context,
  className,
  dismissable = true,
  icon,
  variant = 'soft-blocked',
  promotionalImage,
  showLocalTranscriptionNag = false,
}) => {
  const { tier: creditsTier, openCreditsPurchase } = useCreditsTopUp()
  const user = useUser()

  // Check if user is logged in (not anonymous)
  const isLoggedIn = useMemo(() => {
    if (!user) return false
    if (user.provider === 'workos') return true
    return false
  }, [user])

  // Get the specific context key for dismissal tracking
  const { isPro, paywallState, subscription } = useEnrichedBilling()

  const { isUsingLocalTranscription } = useAudioNoteSettings()
  const atWarning = paywallState.atWarning // 80-99%
  const atLimit = paywallState.atLimit // 100%
  const usagePercentage = Math.round(paywallState.credits.percentage)
  const isAlmostOut = usagePercentage > 90

  // Check if trial was claimed from onboarding

  // Check for local transcription condition (takes priority for audio-note context)
  // Only show in recording overlay (showLocalTranscriptionNag=true), suppress everywhere else to show promo
  // showLocalTranscriptionNag prop from index.tsx handles: actively recording OR (at 100% credits with local transcription)
  // Only legacy paid pro has a CTA here (add credits); free and trial users get no upsell.
  const showLocalTranscriptionNagBar =
    context === 'audio-note' && showLocalTranscriptionNag && isUsingLocalTranscription && paywallState.plan === 'pro'

  // Determine which nag bar to show
  // Both bars only offer Add Credits, so they stay hidden on a plan no top-up
  // can bill.
  const showProWarningNagBar = isPro && creditsTier !== null && atWarning && !showLocalTranscriptionNagBar
  const showProLimitNagBar = isPro && creditsTier !== null && atLimit && !showLocalTranscriptionNagBar
  const getContextKey = () => {
    if (showLocalTranscriptionNagBar) return `${context}-local-transcription`
    if (showProWarningNagBar) return `${context}-pro-warning`
    if (showProLimitNagBar) return `${context}-pro-limit`
    return context
  }

  const contextKey = getContextKey()
  const [isDismissed, setIsDismissed] = useState(isNagBarDismissed(contextKey))

  // Override dismissable when local transcription nag is shown
  const effectiveDismissable = showLocalTranscriptionNagBar ? false : dismissable
  // Helper to format date with ordinal suffix (e.g., "December 3rd, 2025")
  const formatDateWithOrdinal = useCallback((dateString: string | null | undefined): string | null => {
    if (!dateString) return null
    const date = new Date(dateString)
    const day = date.getDate()
    const month = date.toLocaleDateString('en-US', { month: 'long' })
    const year = date.getFullYear()

    const getOrdinalSuffix = (n: number): string => {
      const j = n % 10
      const k = n % 100
      if (j === 1 && k !== 11) return 'st'
      if (j === 2 && k !== 12) return 'nd'
      if (j === 3 && k !== 13) return 'rd'
      return 'th'
    }

    return `${month} ${day}${getOrdinalSuffix(day)}, ${year}`
  }, [])

  // For audio-note context, show for pro users at warning (80%), at limit (100%), or on local transcription
  // Don't show nag bars during dummy chat (onboarding) - but only for chat context
  const shouldShow = showLocalTranscriptionNagBar || showProWarningNagBar || showProLimitNagBar

  // Initialize dismissal state from storage and sync when contextKey changes
  useEffect(() => {
    setIsDismissed(isNagBarDismissed(contextKey))
  }, [contextKey])

  const messagingProWarning = {
    'meeting-summary': {
      message: isAlmostOut
        ? "You're almost out of credits. Add more credits to continue using meeting summaries."
        : "You're running low on credits. Add more credits to continue using meeting summaries.",
      ctaText: 'Add Credits',
      variant: 'warning' as const,
      action: 'addcredits' as const,
    },
    chat: {
      message: isAlmostOut
        ? "You're almost out of credits. Add more credits to keep using the best models."
        : "You're running low on credits. Add more credits to keep using the best models.",
      ctaText: 'Add Credits',
      variant: 'warning' as const,
      action: 'addcredits' as const,
    },
    home: {
      message: isAlmostOut
        ? "You're almost out of credits. Add more credits to keep using the best models."
        : "You're running low on credits. Add more credits to keep using the best models.",
      ctaText: 'Add Credits',
      variant: 'warning' as const,
      action: 'addcredits' as const,
    },
    'audio-note': {
      message: isAlmostOut
        ? "You're almost out of credits. Add more credits to continue generating the highest quality meeting notes."
        : "You're running low on credits. Add more credits to continue generating the highest quality meeting notes.",
      ctaText: 'Add Credits',
      variant: 'warning' as const,
      action: 'addcredits' as const,
    },
  }

  // Get renewal date for Pro limit messaging
  const renewalDate =
    subscription.currentPeriodEndAt && !subscription.cancelled
      ? formatDateWithOrdinal(subscription.currentPeriodEndAt)
      : null

  const messagingProLimit = useMemo(() => {
    const getProLimitDescription = (): string | undefined => {
      if (renewalDate) {
        return `We will auto-select models until your subscription renews. Add credits anytime to restore access to premium models.`
      }
      return "You're now using an auto-selected model. You can add credits to restore access to the best models."
    }

    return {
      'meeting-summary': {
        message: "You've run out of credits. Add more credits to continue using meeting transciption.",
        ctaText: 'Add Credits',
        variant: 'info' as const,
        action: 'addcredits' as const,
      },
      chat: {
        message: "You've used 100% of your Pro credits",
        description: getProLimitDescription(),
        ctaText: 'Add Credits',
        variant: 'soft-blocked' as const,
        action: 'addcredits' as const,
      },
      home: {
        message: "You've used 100% of your Pro credits",
        description: getProLimitDescription(),
        ctaText: 'Add Credits',
        variant: 'soft-blocked' as const,
        action: 'addcredits' as const,
      },
      'audio-note': {
        message: "You've used 100% of your credits",
        description: 'Add credits to continue generating the highest quality meeting notes.',
        ctaText: 'Add Credits',
        variant: 'promotional' as const,
        action: 'addcredits' as const,
      },
    }
  }, [renewalDate, formatDateWithOrdinal])

  // Get message and CTA based on state
  // Check local transcription nag FIRST (highest priority for audio-note context)
  const content = useMemo((): {
    message: string
    description?: string
    ctaText: string
    variant: 'warning' | 'blocked' | 'soft-blocked' | 'free-tier' | 'info' | 'promotional'
    action: 'addcredits'
  } | null => {
    // Check local transcription nag first (highest priority for audio-note context)
    if (showLocalTranscriptionNagBar) {
      return {
        message: 'You are using lower quality local transcription, add credits for the best meeting notes.',
        ctaText: 'Add Credits',
        variant: 'warning' as const,
        action: 'addcredits' as const,
      }
    }
    if (showProWarningNagBar) {
      if (messagingProWarning[context as keyof typeof messagingProWarning]) {
        return messagingProWarning[context as keyof typeof messagingProWarning]
      }
    }
    if (showProLimitNagBar) {
      if (messagingProLimit[context as keyof typeof messagingProLimit]) {
        return messagingProLimit[context as keyof typeof messagingProLimit]
      }
    }
    return null
    // isAlmostOut and messagingProLimit are read through the message maps
    // above, so the copy goes stale without them: crossing 90% does not flip
    // any of the booleans.
  }, [showLocalTranscriptionNagBar, showProWarningNagBar, showProLimitNagBar, context, isAlmostOut, messagingProLimit])

  // Use content variant if available, otherwise use prop variant
  const effectiveVariant = content?.variant || variant

  // Default promotional image for home and chat free plan
  const defaultPromotionalImage =
    'https://storage.googleapis.com/cdn.highlightai.com/media/product/payment/pro-nag-graphic.webp'
  // Summary nag graphic for audio-note context
  const summaryNagImage =
    'https://storage.googleapis.com/cdn.highlightai.com/media/product/payment/summary-nag-graphic.webp'
  const imageToUse =
    promotionalImage ||
    (effectiveVariant === 'promotional'
      ? context === 'audio-note'
        ? summaryNagImage
        : context === 'home' || context === 'chat'
          ? defaultPromotionalImage
          : undefined
      : undefined)

  const handleCta = useCallback(() => {
    if (!content) return

    if (content.action === 'addcredits') {
      window.ampltd.trackEvent('Nag Bar', {
        action: 'add credits clicked',
        context,
        plan: paywallState.plan,
      })
      openCreditsPurchase(`nag bar ${context}`)
    }
  }, [content, context, paywallState.plan, openCreditsPurchase])

  const handleDismiss = useCallback(() => {
    window.ampltd.trackEvent('Nag Bar', {
      action: 'dismissed',
      context,
      plan: paywallState.plan,
    })
    dismissNagBar(contextKey)
    setIsDismissed(true)
  }, [context, paywallState.plan, contextKey])

  // Track when nag bar is shown
  useEffect(() => {
    if (shouldShow && !isDismissed && !paywallState.isLegacyBilling) {
      window.ampltd.trackEvent('Nag Bar', {
        action: 'shown',
        context,
        plan: paywallState.plan,
        variant: content?.variant,
      })
    }
  }, []) // Only track on mount

  const handleProgressClick = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation()
      }
      window.open('highlight://settings/subscription', '_blank')
      window.ampltd.trackEvent('Nag Bar', {
        action: 'progress icon clicked',
        context,
        plan: paywallState.plan,
      })
    },
    [context, paywallState.plan],
  )

  // Don't show nag bars while loading, for legacy billing users, if dismissed, or if user is not logged in
  const isPlanDetermined = paywallState.plan !== undefined
  if (
    !isLoggedIn ||
    paywallState.isLoading ||
    !isPlanDetermined ||
    paywallState.isLegacyBilling ||
    isDismissed ||
    !shouldShow ||
    !content
  ) {
    return null
  }

  const textColor = content.variant === 'warning' ? 'text-warning-subtle' : 'text-danger-subtle'

  const getVariantStyles = () => {
    switch (effectiveVariant) {
      case 'warning':
        const progressIcon = showLocalTranscriptionNagBar ? (
          <span className="text-warning-subtle">
            <YellowWarningIcon size={20} />
          </span>
        ) : context === 'home' || context === 'chat' || context === 'audio-note' ? (
          <Tooltip>
            <Tooltip.Trigger render={<div className="relative flex" />}>
              <CircularProgressIcon size={20} progress={usagePercentage} onClick={handleProgressClick} />
            </Tooltip.Trigger>
            <Tooltip.Content sideOffset={4}>{`${usagePercentage}% used`}</Tooltip.Content>
          </Tooltip>
        ) : (
          <span className="text-warning-subtle">
            <YellowWarningIcon size={20} />
          </span>
        )
        return {
          bg: 'bg-[rgba(255,255,255,0.10)] border border-[rgba(255,255,255,0.10)]',
          text: 'text-white/80',
          icon: progressIcon,
        }
      case 'blocked':
        return {
          bg: effectiveDismissable ? 'bg-red/10' : 'bg-red/10 border-light-5',
          text: 'text-danger-subtle',
          icon: (
            <div className="bg-red/10 flex items-center justify-center rounded-full">
              <CircleAlert className="text-red/60 h-5 w-5" stroke="currentColor" strokeWidth={1.75} fill="none" />
            </div>
          ),
        }
      case 'soft-blocked':
        return {
          bg: 'bg-light-10 border-light-10',
          text: 'text-danger-subtle',
          icon: (
            <Tooltip>
              <Tooltip.Trigger render={<div className="relative flex" />}>
                <CircularProgressIcon size={20} progress={100} onClick={handleProgressClick} />
              </Tooltip.Trigger>
              <Tooltip.Content sideOffset={4}>{`${usagePercentage}% used`}</Tooltip.Content>
            </Tooltip>
          ),
        }
      case 'free-tier':
        return {
          bg: 'bg-blue/10 border-blue/30',
          text: 'text-info-subtle',
          icon: <Info className="h-5 w-5" />,
        }
      case 'info':
        return {
          bg: 'bg-light-10 border-light-20',
          text: 'text-light-80',
          icon: <Info className="h-5 w-5" />,
        }
      case 'promotional':
        return {
          bg: 'bg-[rgba(255,255,255,0.10)] border border-[rgba(255,255,255,0.10)]',
          text: 'text-white/80',
          icon: <Info className="h-5 w-5" />,
        }
    }
  }

  const styles = getVariantStyles()
  const displayIcon = icon || styles.icon

  // Blocked variant always uses vertical layout
  if (effectiveVariant === 'blocked' || effectiveVariant === 'soft-blocked') {
    return (
      <div
        className={twMerge(
          'font-inter border-light-5 mt-4 flex cursor-pointer flex-col gap-3 rounded-xl border p-4 transition-all',
          styles.bg,
          className,
        )}
        onClick={handleCta}
      >
        <div className="flex items-center gap-1.5">
          <div className="shrink-0">{displayIcon}</div>
          <p className="m-0 text-sm font-medium leading-[1.7] tracking-[-0.21px] text-white/80">{content.message}</p>
        </div>

        {content.description && (
          <p className="font-base m-0 text-sm leading-[1.7] tracking-[-0.21px] text-white/60">{content.description}</p>
        )}

        <div className="flex items-center gap-2">
          <Button
            size="small"
            variant="yellow"
            onClick={(e) => {
              e.stopPropagation()
              handleCta()
            }}
          >
            {content.ctaText}
          </Button>
          {effectiveDismissable && (
            <Button
              size="small"
              variant="ghost-neutral"
              onClick={(e) => {
                e.stopPropagation()
                handleDismiss()
              }}
            >
              Maybe Later
            </Button>
          )}
        </div>
      </div>
    )
  }

  // Dismissable style (thin, compact)
  const isWarning = effectiveVariant === 'warning'
  const isPromotional = effectiveVariant === 'promotional'

  return (
    <div
      className={twMerge(
        'font-inter group relative my-2 flex cursor-pointer flex-row items-center justify-between px-2 py-1.5 transition-all',
        isPromotional ? 'gap-4' : 'gap-2',
        isWarning ? 'rounded-lg py-3 shadow-md' : 'rounded-2xl',
        styles.bg,
        className,
      )}
      onClick={handleCta}
    >
      {isPromotional && effectiveDismissable && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            handleDismiss()
          }}
          className="shadow-xs absolute -right-2 -top-2 flex cursor-pointer items-center gap-1.5 rounded-full bg-[#353535] p-0.5 font-medium opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-100"
          aria-label="Dismiss"
        >
          <X className="h-[18px] w-[18px]" strokeWidth={1} />
        </button>
      )}
      {isPromotional && imageToUse ? (
        <div className="h-16 w-auto shrink-0 overflow-hidden rounded-lg">
          <img src={imageToUse} alt="" className="h-16 w-auto rounded-lg object-contain" role="presentation" />
        </div>
      ) : (
        <div className={twMerge('shrink-0 pl-2', styles.text)}>{displayIcon}</div>
      )}
      <div className="flex-1">
        <p
          className={twMerge(
            'font-base m-0 text-sm',
            isWarning ? 'leading-[1.7] tracking-[-0.21px] text-white/70' : styles.text,
          )}
        >
          {content.message}
        </p>
        {content.description && (
          <p
            className={twMerge(
              'text-light-60 m-0 mt-1 text-sm font-normal tracking-[-0.21px]',
              context === 'audio-note' ? 'leading-[1.3]' : 'leading-[1.7]',
            )}
          >
            {content.description}
          </p>
        )}
      </div>

      <div className={twMerge('flex shrink-0 items-center', context === 'chat' ? 'gap-0' : 'gap-2')}>
        <Button
          size={context === 'audio-note' ? 'large' : 'small'}
          variant={effectiveVariant === 'warning' || effectiveVariant === 'promotional' ? 'yellow' : 'danger'}
          onClick={(e) => {
            e.stopPropagation()
            handleCta()
          }}
        >
          {content.ctaText}
        </Button>

        {effectiveDismissable &&
          !isPromotional &&
          (context === 'chat' || context === 'audio-note' ? (
            <Button
              size="small"
              variant="ghost-neutral"
              onClick={(e) => {
                e.stopPropagation()
                handleDismiss()
              }}
              aria-label="Dismiss"
              className="bg-transparent hover:bg-transparent"
            >
              <X className="h-4.5 w-4" strokeWidth={1.5} />
            </Button>
          ) : (
            <Button
              size="small"
              variant="tertiary"
              onClick={(e) => {
                e.stopPropagation()
                handleDismiss()
              }}
              aria-label="Dismiss"
            >
              Maybe Later
            </Button>
          ))}
      </div>
    </div>
  )
}
